<template>
  <div class="full_width">
    <v-btn class="refresh-button" @click="refreshOrders()">
      <v-icon color="#6E6E6E">mdi-sync</v-icon>
    </v-btn>
    <div class="table-style">
      <div class="table-header">
        <div class="d-flex flex-row justify-space-between">
          <div class="pill-radio-buttons-div">
            <PillRadioButtons
              :selectedValue="
                Array.isArray(statusSelection) ? 'all' : statusSelection
              "
              :itemsList="statusValuesList"
              @update-selected-value="statusSelection = $event"
            />
          </div>
          <ConsumptionDevicesDownloadData :dateRangeFilters="dateFilters" />
        </div>
        <div class="pill-radio-buttons-div">
          <v-text-field
            rounded
            outlined
            dense
            full-width
            v-model="searchValue"
            prepend-inner-icon="mdi-magnify"
            @input="elasticSearchOnBackend"
            label="Suche"
            single-line
            hide-details
          />
        </div>
        <PeriodDatePicker :key="rerenderID" @date-change="onDateChange" />
      </div>
      <v-data-table
        disable-sort
        :headers="headersAktive"
        :items="activeOrdersDisplayed"
        item-key="orderId"
        :loading="loading"
        :loading-text="$t('overview_table.loading_text')"
        :no-data-text="
          this.isDataLoaded
            ? $t('overview_table.no_filter_data')
            : $t('overview_table.error_data')
        "
        :footer-props="{
          'items-per-page-options': [10],
        }"
        :server-items-length="filteredActiveOrdersCount"
        calculate-widths
        :options.sync="options"
        :page="currentPage"
      >
        <template v-slot:[`item.date`]="{ item }">
          <span>{{ item.date | formatDate('DD.MM.YYYY') }}</span>
        </template>
        <template v-slot:[`item.status`]="props">
          <v-btn
            rounded
            dark
            :color="
              //color here is for background-color
              props.item && props.item.status && props.item.status.color
                ? props.item.status.color
                : ''
            "
            :style="{
              //color here is for text color
              color:
                props.item && props.item.status && props.item.status.textColor
                  ? props.item.status.textColor
                  : 'white',
              pointerEvents: 'none',
            }"
            >{{
              (props.item && props.item.status && props.item.status.label) || ''
            }}</v-btn
          >
        </template>
        <template v-slot:[`item.edit`]="props">
          <v-btn icon @click="showDetails(props.item.orderId)">
            <v-icon>mdi-pencil-box-outline</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
import {
  PeriodDatePicker,
  PillRadioButtons,
} from '@newnetzportal/netzportal-webapp-storybook-components';
import { getConsumptionDevicesOrdersWithFilter } from '../../services/ConsumptionDevicesService';
import { getConsumptionDevicesTableConfig } from './consumptionDevicesTableConfig';
import { tableOrderMapper } from './transform_raw_data_to_orders_table_format';
import ConsumptionDevicesDownloadData from './ConsumptionDevicesDownloadData.vue';

export default {
  name: 'ConsumptionDevicesOverviewTable',
  components: {
    ConsumptionDevicesDownloadData,
    PeriodDatePicker,
    PillRadioButtons,
  },
  created() {
    this.loading = true;
    this.getConsumptionDevicesOrdersWithFilter();
  },
  data() {
    return {
      statusSelection: this.$route.query.status
        ? this.$route.query.status
        : 'all',
      currentPage: this.$route.query.page
        ? parseInt(this.$route.query.page)
        : 1,
      activeOrdersDisplayed: [],
      loading: false,
      options: {},
      searchValue: this.$route.query.search ? this.$route.query.search : '',
      filterStartDate: null,
      filterEndDate: null,
      rerenderID: 1,
      filteredActiveOrdersCount: 0,
      statusValuesList: getConsumptionDevicesTableConfig('statusValuesList'),
      headersAktive: getConsumptionDevicesTableConfig('headersAktive'),
      isDataLoaded: false,
    };
  },
  computed: {
    dateFilters() {
      return {
        start: this.filterStartDate,
        end: this.filterEndDate,
      };
    },
  },
  watch: {
    $route(to) {
      if (to.fullPath === '/verbrauchseinrichtung') {
        this.refreshOrders();
      }
    },
    statusSelection(newValue) {
      this.currentPage = 1;
      if (newValue !== 'all') {
        this.statusSelection = newValue;
      }
      // TODO: add this to store
      // this.$store.commit('setConsumptionDevicesStatusFilter', newValue);
      this.updateURLParams({ name: 'status', value: newValue });
      this.getConsumptionDevicesOrdersWithFilter();
    },
    searchValue(newValue) {
      this.updateURLParams({ name: 'search', value: newValue });
    },
    options: {
      handler(event) {
        this.updateURLParams({ name: 'page', value: event.page });
        this.currentPage = event.page;
        this.getConsumptionDevicesOrdersWithFilter();
      },
      deep: true,
    },
  },
  methods: {
    onDateChange(values) {
      this.filterStartDate = values.startDate;
      this.filterEndDate = values.endDate;
      this.currentPage = 1;
      this.getConsumptionDevicesOrdersWithFilter();
    },
    updateURLParams(param) {
      const params = this.$route.query;

      if (!param.value) {
        delete params[param.name]; // remove param if empty
      } else {
        params[param.name] = param.value;
      }

      history.replaceState(
        {},
        null,
        `${this.$route.path}?${new URLSearchParams(params)}`
      );
    },
    refreshOrders() {
      const params = this.$route.query;
      params.page = 1;
      params.status = '';
      if (params.search) delete params.search;

      this.statusSelection = '';
      this.searchValue = '';
      this.currentPage = 1;
      this.filterStartDate = null;
      this.filterEndDate = null;
      this.rerenderID++;

      history.replaceState(
        {},
        null,
        `${this.$route.path}?${new URLSearchParams(params)}`
      );

      this.getConsumptionDevicesOrdersWithFilter();
    },
    elasticSearchOnBackend(value) {
      if (value.length === 0 || value.length > 2) {
        this.currentPage = 1;
        this.getConsumptionDevicesOrdersWithFilter();
      }
    },
    getConsumptionDevicesOrdersWithFilter(search_word) {
      const params = {
        limit: 10,
        status: this.statusSelection === 'all' ? '' : this.statusSelection,
        from_index: (this.currentPage - 1) * 10,
        search_word: search_word ? search_word : this.searchValue,
        start_date: this.filterStartDate,
        end_date: this.filterEndDate,
      };

      this.loading = true;

      getConsumptionDevicesOrdersWithFilter(params)
        .then((res) => {
          if (!res || Object.keys(res).length === 0) {
            console.warn('No data received or empty response.');
            this.isDataLoaded = false;
            this.activeOrdersDisplayed = [];
            return;
          }

          this.filteredActiveOrdersCount = res.count || 0;
          const ordersWithStatusConfig = this.addStatusConfigForEachOrder(
            res.items || []
          );

          this.$set(
            this,
            'activeOrdersDisplayed',
            ordersWithStatusConfig.map(tableOrderMapper)
          );
          this.isDataLoaded = true;
        })
        .catch((err) => {
          console.error('Error fetching filtered consumption devices:', err);
          this.isDataLoaded = false;
          this.activeOrdersDisplayed = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    showDetails(id) {
      this.$store.commit('deleteSelectedConsumptionDevicesOrder');
      this.$router.push({
        name: 'ConsumptionDevicesDetailsPage',
        params: { id: id },
      });
    },
    addStatusConfigForEachOrder(ordersArray) {
      ordersArray.forEach((item) => {
        item.status = this.statusValuesList.find(
          (status) => status.value === item.status
        );
      });
      return ordersArray;
    },
  },
};
</script>
